import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { convertUTCToLocal, formatDob } from "../../../utils/helper";

/* ================= HELPERS ================= */

const safe = (v, d = "—") =>
  v === null || v === undefined || v === "" ? d : v;


/* ================= PDF GENERATION ================= */

const generateOORSummaryPDF = ({
  logo,
  reportRange,
  providerName,
  oorRecords = [],
  patientDetails = null,
  escalatedReadingsFrequency = null,
}) => {
 

  const pdf = new jsPDF({
    orientation: "landscape", // Increased page width by using landscape
    unit: "mm",
    format: "a4",
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 8; // Reduced margin for smaller page feel
  const contentWidth = pageWidth - margin * 2;

  let yPosition = margin;

  // Helper to add a new page if needed
  const checkPageBreak = (requiredHeight) => {
    if (yPosition + requiredHeight > pageHeight - 30) {
      pdf.addPage();
      yPosition = margin;
      // Redraw header on new page
      addHeader();
      yPosition = 80; // After header and patient info
    }
  };

  // Add header function matching the image format
  const addHeader = (pageNumber = 1, totalPages = 1) => {
    let y = margin; // Reduced top space

    // Logo on top-left
    let logoWidth = 0;
    let logoHeight = 0;
    if (logo) {
      try {
        logoHeight = 10; // Decreased logo height
        logoWidth = 50; // mm
        pdf.addImage(logo, "PNG", margin, y, logoWidth, logoHeight);
        y += logoHeight + 2;
        
        // Underline below logo
        pdf.setDrawColor(34, 34, 34);
        pdf.setLineWidth(0.5);
        pdf.line(margin, y, margin + logoWidth, y);
        y += 5; // Gap between underline and Dialysis Center
      } catch (e) {
        // Logo image could not be added
      }
    }

    // Dialysis Center text (or office location) - below underline
    if (patientDetails?.location?.name) {
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(136, 136, 136);
      pdf.text(patientDetails.location.name, margin, y);
      y += 5;
    }

    // Title: "Out of Range Summary Report" (left aligned) - with top space
    y += 2; // Reduced space above title
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(34, 34, 34);
    const title = "Out of Range Summary Report";
    pdf.text(title, margin, y);

    y += 6;

    // Patient Information Section
    y += 2; // Reduced spacing before patient info
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(34, 34, 34);

    const patientName =
      patientDetails?.user
        ? `${patientDetails.user.first_name || ""} ${patientDetails.user.last_name || ""}`.trim()
        : oorRecords[0]?.patient_name || "—";
    const dob = patientDetails?.user?.dob
      ? formatDob(patientDetails.user.dob)
      : oorRecords[0]?.dob
      ? formatDob(oorRecords[0].dob)
      : "—";
    const doctor = providerName || "—";
    const subaccount = patientDetails?.location?.name ||  "—";

    // Left column - labels bold in #666666, values normal in black with gap
    pdf.setFont("helvetica", "bold"); // Bold labels
    pdf.setTextColor(102, 102, 102); // #666666
    pdf.text("Patient Name:", margin, y);
    pdf.setFont("helvetica", "normal"); // Normal weight for data
    pdf.setTextColor(0, 0, 0); // Black
    pdf.text(patientName, margin + 35, y);
    
    pdf.setFont("helvetica", "bold"); // Bold labels
    pdf.setTextColor(102, 102, 102); // #666666
    pdf.text("DOB:", margin, y + 5);
    pdf.setFont("helvetica", "normal"); // Normal weight for data
    pdf.setTextColor(0, 0, 0); // Black
    pdf.text(dob, margin + 35, y + 5);

    // Add Percentage below DOB if available
    if (escalatedReadingsFrequency != null) {
      pdf.setFont("helvetica", "bold"); // Bold labels
      pdf.setTextColor(102, 102, 102); // #666666
      pdf.text("Percentage:", margin, y + 10);
      pdf.setFont("helvetica", "normal"); // Normal weight for data
      pdf.setTextColor(0, 0, 0); // Black
      pdf.text(`${escalatedReadingsFrequency}%`, margin + 35, y + 10);
    }

    // Right column - labels bold in #666666, values normal in black with gap (right-aligned like preview)
    // Calculate right column start position (similar to preview's right column)
    const rightColumnStart = pageWidth - margin - 80; // Start position for right column
    
    pdf.setFont("helvetica", "bold"); // Bold labels
    pdf.setTextColor(102, 102, 102); // #666666
    pdf.text("Provider Name:", rightColumnStart, y);
    pdf.setFont("helvetica", "normal"); // Normal weight for data
    pdf.setTextColor(0, 0, 0); // Black
    const doctorX = rightColumnStart + pdf.getTextWidth("Provider Name:") + 8; // Gap after label
    pdf.text(doctor, doctorX, y);
    
    pdf.setFont("helvetica", "bold"); // Bold labels
    pdf.setTextColor(102, 102, 102); // #666666
    pdf.text("Subaccount Name:", rightColumnStart, y + 5);
    pdf.setFont("helvetica", "normal"); // Normal weight for data
    pdf.setTextColor(0, 0, 0); // Black
    const subaccountX = rightColumnStart + pdf.getTextWidth("Subaccount Name:") + 8; // Gap after label
    pdf.text(subaccount, subaccountX, y + 5);

    // Add separator line below patient info
    // Adjust y position based on whether percentage was added
    const patientInfoHeight = escalatedReadingsFrequency != null ? 15 : 10;
    y += patientInfoHeight;
    pdf.setDrawColor(224, 224, 224);
    pdf.setLineWidth(0.3);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 5;

    return y;
  };

 
  // Add footer with title (left), page (center), date (right)
  const addFooter = (pageNumber, totalPages) => {
    const footerY = pageHeight - 10;
    
    // Add line above footer
    pdf.setDrawColor(224, 224, 224);
    pdf.setLineWidth(0.3);
    pdf.line(margin, footerY - 3, pageWidth - margin, footerY - 3);
    
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(102, 102, 102);
    
    const title = "Out of Range Summary Report";
    const pageText = `Page ${pageNumber} of ${totalPages}`;
    const generatedDate =  convertUTCToLocal(new Date().toISOString(), navigator.language, true);
    
    // Title (left)
    pdf.text(title, margin, footerY);
    
    // Page number (center)
    const pageTextWidth = pdf.getTextWidth(pageText);
    pdf.text(pageText, (pageWidth - pageTextWidth) / 2, footerY);
    
    // Generated date (right)
    const dateWidth = pdf.getTextWidth(generatedDate);
    pdf.text(generatedDate, pageWidth - margin - dateWidth, footerY);
  };

  // Add header on first page
  yPosition = addHeader(1);

  // Prepare table data - Date, Type, Value, Category columns (matching Charts.js)
  const tableData = oorRecords.map((r) => [
    r.date ? convertUTCToLocal(r.date) : "—",
    safe(r.type || r.reading_type_display),
    safe(r.value),
    safe(r.category || r.monitoring_category),
  ]);

  // Generate table using autoTable with clean borders
  let totalPages = 1;
  autoTable(pdf, {
    head: [["Date", "Type", "Value", "Category"]],
    body: tableData.length > 0 ? tableData : [["No records", "", "", "", "", "", ""]],
    startY: yPosition,
    margin: { left: margin, right: margin, top: yPosition, bottom: 20 },
    styles: {
      fontSize: 8,
      cellPadding: 3,
      textColor: [0, 0, 0],
      lineColor: [200, 200, 200], // Light grey borders
      lineWidth: 0.3, // Thin borders
    },
    headStyles: {
      fillColor: [237, 231, 243],
      textColor: [43, 43, 43],
      fontStyle: "bold",
      lineColor: [200, 200, 200], // Light grey borders
      lineWidth: 0.3, // Thin borders
    },
    bodyStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      lineColor: [200, 200, 200], // Light grey borders
      lineWidth: 0.3, // Thin borders
    },
    alternateRowStyles: {
      fillColor: [246, 249, 253], // Light blue for alternating rows
      lineColor: [200, 200, 200], // Light grey borders
      lineWidth: 0.3, // Thin borders
    },
    columnStyles: {
      0: { cellWidth: "auto" }, // Date
      1: { cellWidth: "auto" }, // Type
      2: { cellWidth: "auto" }, // Value
      3: { cellWidth: "auto" }, // Category
    },
    tableLineColor: [200, 200, 200], // Light grey for table outline
    tableLineWidth: 0.3, // Thin table outline
    showHead: "everyPage",
    showFoot: "never",
    didDrawPage: (data) => {
      // Ensure totalPages is set correctly
      totalPages = data.totalPages || pdf.internal.pages.length - 1;
      // Redraw header on each new page
      if (data.pageNumber > 1) {
        addHeader(data.pageNumber, totalPages);
      }
      // Add footer with title, page, and date
      addFooter(data.pageNumber, totalPages);
    },
  });

  // Get final total pages count
  totalPages = pdf.internal.pages.length - 1;

  // Add footer to first page as well
  pdf.setPage(1);
  addFooter(1, totalPages);

  return pdf;
};

export default generateOORSummaryPDF;
